import { useState, useEffect } from 'react';
import { useLanguage, useTheme } from '../../providers/preferenceProvider';
import '../../css/overlay.css';
import { getRoomsPlain } from '../../api/room-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGlobe, faPalette, faHouse, faClock } from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../../providers/toastProvider';

const PreferencesPopup = ({ isOpen, onClose, onRoomSelect }) => {
  const { showToast } = useToast();
  const { t, lang, getAvailableLanguages, changeLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();

  const [localLang, setLocalLang] = useState(lang);
  const [localTheme, setLocalTheme] = useState(theme);
  const [langs, setLangs] = useState([]);
  const [userRooms, setUserRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [timelineOptions, setTimelineOptions] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState('');

  useEffect(() => {
    const room = userRooms.find(r => r.id.toString() === selectedRoomId);
    if (room && room.interval) {
      const interval = parseInt(room.interval, 10);
      const blocksPerHour = 60 / interval;
      const maxHours = Math.floor(16 / blocksPerHour);
  
      const options = [];
      for (let hour = 1; hour <= maxHours; hour++) {
        options.push(hour);
      }
  
      setTimelineOptions(options);
    } else {
      setTimelineOptions([]);
    }
  }, [selectedRoomId, userRooms]);
  
  useEffect(() => {
    if (!isOpen) return;
    setLocalLang(lang);
    setLocalTheme(theme);
    setLangs(getAvailableLanguages());

    getRoomsPlain()
      .then(response => {
        const rooms = response.data || [];
        setUserRooms(rooms);
        if (rooms.length > 0) {
          setSelectedRoomId('');
        }
      })
      .catch(error => {
        console.error('Failed to fetch rooms:', error);
      });
  }, [isOpen, lang, theme, getAvailableLanguages]);

  if (!isOpen) return null;

  const closePopup = () => {
    onClose();
  };

  const confirmChanges = () => {
    if (!localLang || !localTheme || !selectedRoomId || !selectedTimeline) {
      showToast(t.toast.required_input_fields, 'error');
      return;
    }
    setTheme(localTheme);
    changeLanguage(localLang);
  
    if (onRoomSelect) {
      onRoomSelect(selectedRoomId, selectedTimeline);
    }
  
    closePopup();
  };
  
  return (
    <div className="overlay-backdrop" >
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">{t.preferences || "Preferences"}</div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmChanges();
          }}
          className="overlay-admin-form flex flex-col gap-4 p-4"
        >
          {/* Language select */}
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="language" className="w-32"><FontAwesomeIcon icon={faGlobe} /> {t.language}</label>
            <select
              id="language"
              value={localLang}
              onChange={(e) => {
                const newLang = e.target.value;
                setLocalLang(newLang);
                changeLanguage(newLang);
              }}
              className="overlay-admin-input"
            >
              {langs.map((code) => (
                <option key={code} value={code}>
                  {t.language_codes?.[code] || code}
                </option>
              ))}
            </select>
          </div>

          {/* Theme select */}
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="theme" className="w-32"><FontAwesomeIcon icon={faPalette} /> {t.theme}</label>
            <select
              id="theme"
              value={localTheme}
              onChange={(e) => {
                const newTheme = e.target.value;
                setLocalTheme(newTheme);
                setTheme(newTheme);
              }}
              className="overlay-admin-input"
            >
              <option value="light">{t.themes.light_mode}</option>
              <option value="dark">{t.themes.dark_mode}</option>
              <option value="barbie">{t.themes.barbie_mode}</option>
              <option value="smurf">{t.themes.smurf_mode}</option>
              <option value="shrek">{t.themes.shrek_mode}</option>
            </select>
          </div>

          {/* Room select */}
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="room" className="w-32"> <FontAwesomeIcon icon={faHouse} /> {t.room || "Room"}</label>
            <select
              id="room"
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              className="overlay-admin-input"
              required
            >
              <option value="">{t.select || "Select"}</option>
              {userRooms.map((room) => {
                if (room.active) {
                  return (
                    <option key={room.id} value={room.id.toString()}>
                      {room.name}
                    </option>
                  );
                }
                return null; 
              })}
            </select>
          </div>

          {/* Timeline select */}
          {selectedRoomId  && (
            <div className="flex flex-row items-center gap-4">
              <label htmlFor="timeline" className="w-32"><FontAwesomeIcon icon={faClock} /> {t.timeline}</label>
              <select
                id="timeline"
                value={selectedTimeline}
                onChange={(e) => setSelectedTimeline(e.target.value)}
                className="overlay-admin-input"
                required
              >
                <option value="">{t.select || "Select"}</option>
                {timelineOptions.map((length) => (
                  <option key={length} value={length}>
                    {length} {t.hours}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-row-reverse mt-4 gap-2">
            <button type="submit" className="btn">{t.confirm || "Confirm"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreferencesPopup;